import _ from 'lodash';
import $ from 'jquery';

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  makeStyles,
} from '@material-ui/core';
import { setAddedInvoiceStatus } from 'state/slices/listings';
import { useAuth, useListing } from 'hooks';
import {
  hideModal,
  createInvoiceForAdditionalServiceAsync,
  brokerPurchasedPicturesAsync,
  fetchListingAsync,
} from 'state/actions';
import { MODAL } from 'config/constants';
import { HvModal, HvButton, HvPleaseWait, HvDetailedBill } from 'components';
import { getPictureServiceOfferIdsCopy } from 'utils';
import { Picture } from 'models';
import { isMobile } from 'utils/screen';
import { ThumbnailList } from '../components';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 550,
    '@media (max-width: 767px)': {
      minWidth: 'auto',
    },
    maxWidth: 700,
  },
  description: {
    fontSize: 14,
  },
  button: {
    width: 200,
    marginLeft: 10,
  },
  thumbnailList: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    flexGrow: 1,
    overflowY: 'auto',
    maxHeight: 400,
    maxWidth: 685,
    overflowX: 'hidden',
  },
  cancelButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  summary: {
    fontSize: 12,
  },
  sectionTitle: {
    color: 'white',
    fontSize: 17,
    backgroundColor: '#00bdf2',
    padding: '5px 20px 1px 20px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  sectionBox: {
    border: 'solid 4px #00bdf2',
    borderRadius: 3,
    marginTop: 10,
    '& div:first-child': {
      marginRight: 0,
    },
    '@media (max-width: 767px)': {
      minWidth: 'auto',
      marginBottom: 20,
    },
  },
  summaryItemBox: {
    maxWidth: 500,
    paddingTop: 10,
  },
  summaryItems: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 'bold',
    padding: 5,
  },
});

interface AdditionalPicturesModalProps {
  data: any;
}

function AdditionalPicturesModal(props: AdditionalPicturesModalProps) {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();
  const { data } = props;
  const { pictures, listing } = data;
  const { token } = useAuth();
  const path = window.location.pathname;
  const arr = path.split('/');
  const distributionToken = arr[arr.length - 1];
  const { addedInvoiceStatus, addedPurchasedPicturesStatus, loading } =
    useListing(distributionToken);
  const [size, setSize] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [load, setLoad] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogText, setDialogText] = useState('');
  const [selectedPictures, setSelectedPictures] = useState([]);
  const [pictureServiceOfferIds, setPictureServiceOfferIds] = useState({
    traditional: { id: 0, price: 0 },
    drone: { id: 0, price: 0 },
    blueHour: { id: 0, price: 0 },
  });
  const [activeSections, setActiveSections] = useState({
    traditional: false,
    drone: false,
    blueHour: false,
  });
  const [selectedPictureCount, setSelectedPictureCount] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [thumbnailsContentWidth, setThumbnailsContentWidth] = useState(0);

  const fmtDialogTitle = intl.formatMessage({ id: 'deliver.additionalPictures.dialogTitle' });
  const fmtDialogFail = intl.formatMessage({ id: 'deliver.additionalPictures.dialogTitleFailed' });
  const fmtDialogText = intl.formatMessage({ id: 'deliver.additionalPictures.dialogText' });
  const fmtDialogTextUnedited = intl.formatMessage({
    id: 'deliver.additionalPictures.dialogTextUnedited',
  });
  const fmtDialogTextFail = intl.formatMessage({
    id: 'deliver.additionalPictures.dialogTextFailed',
  });

  const resizeThumbnails = _.debounce(() => {
    const bodyWidth = $('#root').eq(0).width();
    const width = bodyWidth > 500 ? 222 : bodyWidth / 2 - 5;
    const height = width / 1.5;
    setSize({ width, height });
    const count = Math.floor(bodyWidth / width);
    const thumbnailsWidth = count * width;
    setThumbnailsContentWidth(thumbnailsWidth);
    const thumbnails = $('#thumbnails');
    if (!!thumbnails.length && thumbnailsWidth !== thumbnails.eq(0).width()) {
      thumbnails.eq(0).width(thumbnailsWidth);
    }
  }, 100);

  useEffect(() => {
    resizeThumbnails();
    window.addEventListener('resize', resizeThumbnails);
    return () => {
      window.removeEventListener('resize', resizeThumbnails);
    };
  }, [pictures]);

  useEffect(() => {
    const pictureServiceOfferIdsCopy = pictureServiceOfferIds;
    const data = getPictureServiceOfferIdsCopy(
      pictureServiceOfferIdsCopy,
      listing.broker.subscription_id,
    );
    setPictureServiceOfferIds(data);
  });

  useEffect(() => {
    if (pictures.length === 0) {
      return;
    }
    const pictureCount = {};
    for (const picture of pictures) {
      const { id, price } = picture.service_offer;

      if (id === pictureServiceOfferIds.traditional.id) {
        activeSections.traditional = true;
        pictureServiceOfferIds.traditional.price = price;
      } else if (id === pictureServiceOfferIds.drone.id) {
        activeSections.drone = true;
        pictureServiceOfferIds.drone.price = price;
      } else if (id === pictureServiceOfferIds.blueHour.id) {
        activeSections.blueHour = true;
        pictureServiceOfferIds.blueHour.price = price;
      }

      if (activeSections.traditional && activeSections.drone && activeSections.blueHour) {
        break;
      }
    }
    Object.keys(activeSections).forEach((key: string) => {
      if (activeSections[key]) {
        pictureCount[pictureServiceOfferIds[key].id] = {
          count: 0,
          pricing: pictureServiceOfferIds[key].price,
          name: key,
        };
      }
    });
    setSelectedPictureCount(pictureCount);
  }, [pictureServiceOfferIds, pictures]);

  useEffect(() => {
    if (addedInvoiceStatus.status) {
      if (addedInvoiceStatus.status === 200) {
        setLoad(false);
        const pictureIds = selectedPictures.map((picture: Picture) => {
          return picture.id;
        });
        const isUnedited = selectedPictures[0].isUndedited;
        dispatch(
          brokerPurchasedPicturesAsync(
            token,
            addedInvoiceStatus.invoice_id,
            pictureIds,
            isUnedited,
          ),
        );
        setDialogOpen(true);
        setDialogTitle(fmtDialogTitle);
        setDialogText(isUnedited ? fmtDialogTextUnedited : fmtDialogText);
      } else {
        setDialogOpen(true);
        setDialogTitle(fmtDialogFail);
        setDialogText(fmtDialogTextFail);
      }
    }
  }, [addedInvoiceStatus]);

  useEffect(() => {
    if (addedInvoiceStatus.status) {
      if (addedInvoiceStatus.status === 200) {
        dispatch(fetchListingAsync(distributionToken));
      }
    }
  }, [addedPurchasedPicturesStatus]);

  const onClose = () => {
    dispatch(hideModal(MODAL.ADDITIONAL_PICTURES));
    setSelectedPictures([]);
  };

  async function submitOrder() {
    dispatch(
      createInvoiceForAdditionalServiceAsync(
        token,
        listing,
        selectedPictures,
        'additional_pictures',
      ),
    );
  }

  function addSelectedPicture(picture: Picture) {
    const selectedPicturesCopy = [...selectedPictures];
    if (selectedPicturesCopy.includes(picture)) {
      selectedPicturesCopy.splice(selectedPicturesCopy.indexOf(picture), 1);
      selectedPictureCount[picture.service_offer.id].count--;
    } else {
      selectedPicturesCopy.push(picture);
      selectedPictureCount[picture.service_offer.id].count++;
    }
    setSelectedPictures(selectedPicturesCopy);
  }

  const buttons = () => {
    if (activeStep === 0) {
      return (
        <Box>
          <HvButton className={styles.cancelButton} onClick={onClose}>
            {intl.formatMessage({ id: 'deliver.photos.cancel' })}
          </HvButton>

          <HvButton
            disabled={selectedPictures.length <= 0}
            onClick={() => {
              setActiveStep(1);
            }}
          >
            {intl.formatMessage({ id: 'deliver.photos.next' })}
          </HvButton>
        </Box>
      );
    }

    return (
      <Box>
        <HvButton
          className={styles.cancelButton}
          onClick={() => {
            setActiveStep(0);
          }}
        >
          {intl.formatMessage({ id: 'deliver.photos.back' })}
        </HvButton>

        <HvButton
          onClick={() => {
            submitOrder();
            setLoad(true);
          }}
        >
          {intl.formatMessage({ id: 'deliver.photos.submit' })}
        </HvButton>
      </Box>
    );
  };

  const reviewTab = () => {
    const service_offers = selectedPictures.map((selectedPicture) => {
      return selectedPicture.service_offer;
    });
    return (
      <>
        <HvDetailedBill service_offers={service_offers} />
      </>
    );
  };

  function loadSection(sectionTitle: string, service_offer: any) {
    const label = `${service_offer.price}$${intl.formatMessage({
      id: 'deliver.additionalPictures.each',
    })}`;
    return (
      <>
        <Box className={styles.sectionBox}>
          <Typography className={styles.sectionTitle}>
            {intl.formatMessage({ id: `deliver.additionalPictures.${sectionTitle}` })}
            <span>{label}</span>
          </Typography>
          <Box className={styles.thumbnailList} id='thumbnails'>
            {pictures
              .filter((picture: Picture) => {
                return picture.service_offer.id === service_offer.id;
              })
              .map((pic: Picture) => {
                return (
                  <ThumbnailList
                    key={pic.id}
                    picture={pic}
                    selectedPictures={selectedPictures}
                    addSelectedPicture={() => {
                      addSelectedPicture(pic);
                    }}
                    type='additionalPicturesList'
                    size={size}
                    bodyWidth={thumbnailsContentWidth}
                  />
                );
              })}
          </Box>
        </Box>
      </>
    );
  }

  const pictureOrder = (
    <Box className={styles.content}>
      <Typography className={styles.description}>
        {intl.formatMessage({ id: 'deliver.additionalPictures.subtitle' })}
      </Typography>
      {activeSections.traditional
        ? loadSection('traditionalImgTitle', pictureServiceOfferIds.traditional)
        : null}
      {activeSections.drone ? loadSection('droneImgTitle', pictureServiceOfferIds.drone) : null}
      {activeSections.blueHour
        ? loadSection('blueHourImgTitle', pictureServiceOfferIds.blueHour)
        : null}
    </Box>
  );

  function loadContent() {
    if (activeStep === 0) {
      return pictureOrder;
    }
    return reviewTab();
  }

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>{dialogText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              dispatch(setAddedInvoiceStatus({}));
              onClose();
            }}
            className={styles.button}
            variant='contained'
            color='primary'
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <HvModal
        title={intl.formatMessage({ id: 'deliver.additionalPictures.titleModal' })}
        buttons={pictures.length > 0 ? buttons() : null}
        onClose={onClose}
        largerWidth={!isMobile()}
      >
        {pictures.length > 0 && loadContent()}
        {load && <HvPleaseWait />}
      </HvModal>
    </>
  );
}
export default AdditionalPicturesModal;
